// ============================================================
// PLUMME — AI Rådgiver: salg og lønnsomhet
// ============================================================
// Ren avledning av bokførte fakturaer. Ingen Firebase-kall, ingen mutasjoner —
// samme mønster som deriveReports/deriveBudget: backend.jsx mater inn state,
// denne fila eier regnestykket, og siden viser bare resultatet.
//
// Profit is shown only when every invoiced line has a cost snapshot.
const PlummeAiAdvisorSales = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const round1 = (value) => Math.round(num(value) * 10) / 10;
  const daysBetween = (from, to) => {
    const a = new Date(from);
    const b = new Date(to);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    return Math.round((b - a) / 86400000);
  };
  const percentChange = (current, previous) =>
    previous ? round1(((current - previous) / Math.abs(previous)) * 100) : null;

  // Rabatt regnes av bruttoverdien før rabatt, ikke av linjesummen etter. Ellers
  // ville 100 % rabatt gitt divisjon pa null og 50 % rabatt sett ut som 100 %.
  const lineListPrice = (line) => Math.max(0, num(line.quantity, 0) * num(line.unitPrice, 0));
  const lineDiscount = (line) => {
    const base = lineListPrice(line);
    if (!base) return 0;
    const value = num(line.discountValue, 0);
    if (!value) return 0;
    return line.discountType === 'amount'
      ? Math.min(base, value)
      : Math.min(base, base * (Math.min(100, value) / 100));
  };
  const lineCost = (line) => {
    if (line.inventoryCostMethod === 'weighted_average' && line.inventoryCostAmount != null && Number.isFinite(Number(line.inventoryCostAmount))) return Number(line.inventoryCostAmount);
    if (line.advisoryUnitCost != null && Number.isFinite(Number(line.advisoryUnitCost)) && Number(line.advisoryUnitCost) >= 0) return Math.abs(num(line.quantity)) * Number(line.advisoryUnitCost);
    return null;
  };

  const customerKeyOf = (invoice) =>
    invoice.customer?.id || invoice.customer?.name || invoice.customerName || 'Ukjent kunde';
  const customerNameOf = (invoice) =>
    invoice.customer?.name || invoice.customerName || 'Ukjent kunde';

  // `invoices` er allerede filtrert til bokforte salgsfakturaer for aret av kalleren,
  // som eier isPostedInvoice/reportDateInRange.
  const buildCustomers = ({ invoices, previousInvoices, netOf, lineNetOf, paidDateOf }) => {
    const previousByKey = new Map();
    safeArray(previousInvoices).forEach((invoice) => {
      const key = customerKeyOf(invoice);
      previousByKey.set(key, (previousByKey.get(key) || 0) + netOf(invoice));
    });
    const byKey = new Map();
    safeArray(invoices).forEach((invoice) => {
      const key = customerKeyOf(invoice);
      const entry = byKey.get(key) || {
        key, name: customerNameOf(invoice), revenue: 0, jobs: 0,
        listPrice: 0, discount: 0, paymentDays: [], cost: 0, costMissing: false,
      };
      entry.revenue += netOf(invoice);
      entry.jobs += invoice.kind === 'credit' ? 0 : 1;
      safeArray(invoice.lines).forEach((line) => {
        entry.listPrice += lineListPrice(line) * (invoice.kind === 'credit' ? -1 : 1);
        entry.discount += lineDiscount(line) * (invoice.kind === 'credit' ? -1 : 1);
        if (lineNetOf(line) !== 0) {
          const cost = lineCost(line);
          if (cost == null) entry.costMissing = true;
          else entry.cost += cost * (invoice.kind === 'credit' ? -1 : 1);
        }
      });
      const paidAt = paidDateOf(invoice);
      if (invoice.kind !== 'credit' && paidAt && invoice.date) {
        const days = daysBetween(invoice.date, paidAt);
        // Negative dager betyr forskuddsbetaling for fakturadato — tas med som 0,
        // ikke som et negativt snitt som drar hele kunden nedover.
        if (days != null) entry.paymentDays.push(Math.max(0, days));
      }
      byKey.set(key, entry);
    });
    return [...byKey.values()]
      .map((entry) => {
        const previousRevenue = previousByKey.get(entry.key) || 0;
        const change = percentChange(entry.revenue, previousRevenue);
        return {
          key: entry.key,
          name: entry.name,
          revenue: Math.round(entry.revenue),
          jobs: entry.jobs,
          discountPct: entry.listPrice > 0 ? round1((entry.discount / entry.listPrice) * 100) : 0,
          // null betyr «ingen betalte fakturaer enna», ikke «betaler pa 0 dager».
          avgPaymentDays: entry.paymentDays.length
            ? Math.round(entry.paymentDays.reduce((sum, d) => sum + d, 0) / entry.paymentDays.length)
            : null,
          paidCount: entry.paymentDays.length,
          previousRevenue: Math.round(previousRevenue),
          revenueChangePct: change,
          trend: change == null ? 'new' : change > 5 ? 'up' : change < -5 ? 'down' : 'flat',
          grossProfit: entry.costMissing ? null : Math.round(entry.revenue - entry.cost),
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  };

  const buildProducts = ({ invoices, previousInvoices, lineNetOf }) => {
    const collect = (list) => {
      const byName = new Map();
      safeArray(list).forEach((invoice) => {
        safeArray(invoice.lines).forEach((line) => {
          const name = String(line.productName || line.description || '').trim() || 'Uten produktnavn';
          const entry = byName.get(name) || { name, revenue: 0, count: 0, cost: 0, costMissing: false };
          entry.revenue += lineNetOf(line) * (invoice.kind === 'credit' ? -1 : 1);
          entry.count += invoice.kind === 'credit' ? 0 : 1;
          if (lineNetOf(line) !== 0) {
            const cost = lineCost(line);
            if (cost == null) entry.costMissing = true;
            else entry.cost += cost * (invoice.kind === 'credit' ? -1 : 1);
          }
          byName.set(name, entry);
        });
      });
      return byName;
    };
    const current = collect(invoices);
    const previous = collect(previousInvoices);
    return [...current.values()]
      .map((entry) => {
        const previousRevenue = previous.get(entry.name)?.revenue || 0;
        const change = percentChange(entry.revenue, previousRevenue);
        return {
          name: entry.name,
          revenue: Math.round(entry.revenue),
          lineCount: entry.count,
          previousRevenue: Math.round(previousRevenue),
          revenueChangePct: change,
          marginPct: entry.costMissing || entry.revenue <= 0 ? null : round1(((entry.revenue - entry.cost) / entry.revenue) * 100),
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  };

  const buildSalesModel = ({
    year,
    invoices = [],
    previousInvoices = [],
    netOf,
    lineNetOf,
    paidDateOf,
    recurringCount = 0,
    recurringRevenue = 0,
  }) => {
    const customers = buildCustomers({ invoices, previousInvoices, netOf, lineNetOf, paidDateOf });
    const products = buildProducts({ invoices, previousInvoices, lineNetOf });
    const revenue = customers.reduce((sum, c) => sum + c.revenue, 0);
    const previousRevenue = safeArray(previousInvoices).reduce((sum, invoice) => sum + netOf(invoice), 0);
    const invoiceCount = customers.reduce((sum, c) => sum + c.jobs, 0);
    const listPrice = safeArray(invoices).reduce(
      (sum, invoice) => sum + safeArray(invoice.lines).reduce((s, line) => s + lineListPrice(line) * (invoice.kind === 'credit' ? -1 : 1), 0), 0);
    const discount = safeArray(invoices).reduce(
      (sum, invoice) => sum + safeArray(invoice.lines).reduce((s, line) => s + lineDiscount(line) * (invoice.kind === 'credit' ? -1 : 1), 0), 0);
    const paidDays = customers.flatMap((c) => c.avgPaymentDays == null ? [] : Array(c.paidCount).fill(c.avgPaymentDays));
    const repeatCustomers = customers.filter((c) => c.jobs > 1).length;
    const top = customers[0];
    return {
      year,
      revenue,
      previousRevenue,
      invoiceCount,
      customerCount: customers.length,
      customers,
      products,
      // Rabatt i kroner, ikke bare prosent: rad og scenarioer trenger belopet for a
      // kunne si hva halvering faktisk er verdt.
      listPrice: Math.round(listPrice),
      discountAmount: Math.round(discount),
      recurringRevenue: Math.round(num(recurringRevenue)),
      // Gjor mangelen til en del av kontrakten, sa UI ikke kan glemme a nevne den.
      grossProfitAvailable: customers.length > 0 && customers.every((customer) => customer.grossProfit != null),
      kpis: {
        revenueChangePct: percentChange(revenue, previousRevenue),
        avgInvoice: invoiceCount ? Math.round(revenue / invoiceCount) : 0,
        avgDiscountPct: listPrice > 0 ? round1((discount / listPrice) * 100) : 0,
        avgPaymentDays: paidDays.length
          ? Math.round(paidDays.reduce((sum, d) => sum + d, 0) / paidDays.length)
          : null,
        repeatRatePct: customers.length ? round1((repeatCustomers / customers.length) * 100) : 0,
        topCustomerSharePct: revenue > 0 && top ? round1((top.revenue / revenue) * 100) : null,
        topCustomerName: top?.name || null,
        recurringSharePct: revenue > 0 ? round1((recurringRevenue / revenue) * 100) : 0,
        recurringCount,
      },
    };
  };

  const mergeImportedSummary = (nativeModel, current = null, previous = null) => {
    if (!current?.documentCount) return nativeModel;
    const previousByKey = new Map(safeArray(previous?.customers).map((customer) => [customer.key, customer]));
    const customers = new Map(safeArray(nativeModel?.customers).map((customer) => [customer.key, { ...customer }]));
    safeArray(current.customers).forEach((imported) => {
      const existing = customers.get(imported.key) || {
        key: imported.key, name: imported.name, revenue: 0, jobs: 0, discountPct: 0,
        avgPaymentDays: null, paidCount: 0, previousRevenue: 0, grossProfit: null,
      };
      const paidCount = num(existing.paidCount) + num(imported.paidCount);
      const paidDaysTotal = (num(existing.avgPaymentDays) * num(existing.paidCount)) + num(imported.paidDaysTotal);
      const previousRevenue = num(existing.previousRevenue) + num(previousByKey.get(imported.key)?.revenue);
      const revenue = num(existing.revenue) + num(imported.revenue);
      const change = percentChange(revenue, previousRevenue);
      customers.set(imported.key, {
        ...existing,
        name: existing.name || imported.name,
        revenue: Math.round(revenue),
        grossProfit: null,
        jobs: num(existing.jobs) + num(imported.jobs),
        paidCount,
        avgPaymentDays: paidCount ? Math.round(paidDaysTotal / paidCount) : null,
        previousRevenue: Math.round(previousRevenue),
        revenueChangePct: change,
        trend: change == null ? 'new' : change > 5 ? 'up' : change < -5 ? 'down' : 'flat',
      });
    });
    const products = new Map(safeArray(nativeModel?.products).map((product) => [product.name.toLowerCase(), { ...product }]));
    const previousProducts = new Map(safeArray(previous?.products).map((product) => [String(product.name).toLowerCase(), product]));
    safeArray(current.products).forEach((imported) => {
      const productKey = String(imported.name).toLowerCase();
      const existing = products.get(productKey) || { name: imported.name, revenue: 0, lineCount: 0, previousRevenue: 0, marginPct: null };
      const revenue = num(existing.revenue) + num(imported.revenue);
      const previousRevenue = num(existing.previousRevenue) + num(previousProducts.get(productKey)?.revenue);
      products.set(productKey, {
        ...existing,
        revenue: Math.round(revenue),
        marginPct: null,
        lineCount: num(existing.lineCount) + num(imported.lineCount),
        previousRevenue: Math.round(previousRevenue),
        revenueChangePct: percentChange(revenue, previousRevenue),
      });
    });
    const customerRows = [...customers.values()].sort((a, b) => b.revenue - a.revenue);
    const revenue = num(nativeModel?.revenue) + num(current.revenue);
    const previousRevenue = num(nativeModel?.previousRevenue) + num(previous?.revenue);
    const invoiceCount = num(nativeModel?.invoiceCount) + num(current.invoiceCount);
    const paidCount = customerRows.reduce((total, customer) => total + num(customer.paidCount), 0);
    const paidDaysTotal = customerRows.reduce((total, customer) => total + num(customer.avgPaymentDays) * num(customer.paidCount), 0);
    const repeatCustomers = customerRows.filter((customer) => customer.jobs > 1).length;
    return {
      ...nativeModel,
      grossProfitAvailable: false,
      revenue,
      previousRevenue,
      invoiceCount,
      customerCount: customerRows.length,
      customers: customerRows,
      products: [...products.values()].sort((a, b) => b.revenue - a.revenue),
      importedHistory: {
        documentCount: num(current.documentCount),
        creditNoteCount: num(current.creditNoteCount),
        coverage: current.coverage || 'invoice-totals',
        firstDate: current.firstDate || '',
        lastDate: current.lastDate || '',
      },
      kpis: {
        ...(nativeModel?.kpis || {}),
        revenueChangePct: percentChange(revenue, previousRevenue),
        avgInvoice: invoiceCount ? Math.round(revenue / invoiceCount) : 0,
        avgPaymentDays: paidCount ? Math.round(paidDaysTotal / paidCount) : null,
        repeatRatePct: customerRows.length ? round1((repeatCustomers / customerRows.length) * 100) : 0,
        topCustomerSharePct: revenue > 0 && customerRows[0] ? round1((customerRows[0].revenue / revenue) * 100) : null,
        topCustomerName: customerRows[0]?.name || null,
      },
    };
  };

  return { buildSalesModel, buildCustomers, buildProducts, mergeImportedSummary, lineListPrice, lineDiscount };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorSales = PlummeAiAdvisorSales;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorSales;
